<template>
  <div ref="turnstileContainer" class="turnstile-container"></div>
</template>

<script>
export default {
  name: 'Turnstile',
  props: {
    siteKey: {
      type: String,
      required: true
    },
    theme: {
      type: String,
      default: 'auto'
    },
    size: {
      type: String,
      default: 'normal'
    },
    // 'interaction-only' keeps the widget invisible unless Cloudflare actually
    // needs the visitor to interact. Callers that already show a visible widget
    // elsewhere on the page use it so the page does not sprout a second one.
    appearance: {
      type: String,
      default: 'always'
    }
  },
  data() {
    return {
      widgetId: null,
      retryCount: 0,
      // api.js is a third-party async defer script; 10 x 200ms gave up after 2s,
      // which an ordinary mobile connection exceeds routinely. Giving up emits
      // 'error', and for the card element that means no token and no payment.
      maxRetries: 50,
      retryDelay: 200
    }
  },
  mounted() {
    this.waitForTurnstileAndRender()
  },
  beforeDestroy() {
    if (this.widgetId && window.turnstile) {
      try {
        window.turnstile.remove(this.widgetId)
      } catch (e) {
        console.warn('Failed to remove Turnstile widget:', e)
      }
    }
  },
  methods: {
    waitForTurnstileAndRender() {
      if (window.turnstile && window.turnstile.render) {
        this.renderTurnstile()
      } else if (this.retryCount < this.maxRetries) {
        this.retryCount++
        setTimeout(() => {
          this.waitForTurnstileAndRender()
        }, this.retryDelay)
      } else {
        console.error('Turnstile script failed to load after maximum retries')
        this.$emit('error', 'Turnstile script not available')
      }
    },
    renderTurnstile() {
      if (!window.turnstile || !window.turnstile.render) {
        console.error('Turnstile script not loaded')
        this.$emit('error', 'Turnstile not available')
        return
      }

      try {
        this.widgetId = window.turnstile.render(this.$refs.turnstileContainer, {
          sitekey: this.siteKey,
          theme: this.theme,
          size: this.size,
          appearance: this.appearance,
          callback: this.onSuccess,
          'error-callback': this.onError,
          'expired-callback': this.onExpired
        })
        this.$emit('rendered')
      } catch (error) {
        console.error('Failed to render Turnstile:', error)
        this.$emit('error', error)
      }
    },
    onSuccess(token) {
      this.$emit('verified', token)
    },
    onError(error) {
      console.error('Turnstile error:', error)
      this.$emit('error', error)
    },
    onExpired() {
      console.warn('Turnstile token expired')
      this.$emit('expired')
      this.$emit('verified', null)
    },
    // Returns true only when the re-solve was actually handed to Cloudflare.
    // A false return is a synchronous failure the caller must react to -- silently
    // swallowing it used to leave the card element waiting for a token that could
    // never arrive. Note that a true return is NOT a promise of a token: reset()
    // can be a no-op inside Cloudflare, which is why callers also need a timeout.
    reset() {
      if (!this.widgetId || !window.turnstile) {
        console.warn('Turnstile reset requested before the widget was ready')
        this.$emit('error', 'reset-unavailable')
        return false
      }

      try {
        window.turnstile.reset(this.widgetId)
        this.$emit('verified', null)
        return true
      } catch (e) {
        console.warn('Failed to reset Turnstile widget:', e)
        this.$emit('error', e)
        return false
      }
    }
  }
}
</script>

<style scoped>
.turnstile-container {
  display: flex;
  justify-content: center;
  margin: 10px 0;
}
</style>